import React, { useMemo, useState } from 'react';
import { AppBreadcrumb, AppCard, AppContainer } from '@/components/atoms';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CreateClinicForm } from '@/components/organisms';
import type { CreateClinicFormData } from '@/pages/admin/admin-clinics/CreateClinicForm';
import { useAdminCreateClinic } from '@/hooks/admin/useAdminClinics';
import useAppToast from '@/hooks/useAppToast';

type ValidationErrorResponse = {
  message?: string;
  errors?: Record<string, string[]>;
};

type ErrorWithResponse = {
  response?: {
    data?: ValidationErrorResponse;
  };
};

const CreateClinicPage: React.FC = () => {
  const navigate = useNavigate();
  const { site } = useParams<{ site: string }>();
  const createClinicMutation = useAdminCreateClinic();
  const toast = useAppToast();

  const [isLoading, setIsLoading] = useState(false);
  const [apiFieldErrors, setApiFieldErrors] = useState<Record<string, string> | null>(null);

  const isSuperAdminRoute = site === 'super-admin';
  const siteForBreadcrumb = site ?? 'medix';

  const breadcrumbItems = useMemo(
    () => [
      {
        title: (
          <Link to={`/${siteForBreadcrumb}/admin/clinics`} style={{ color: 'inherit' }}>
            治療院
          </Link>
        ),
      },
      { title: '新規' },
    ],
    [siteForBreadcrumb]
  );

  const handleSubmit = async (data: CreateClinicFormData) => {
    if (!site) {
      toast.error('サイト情報が取得できませんでした。');
      return;
    }

    try {
      setIsLoading(true);

      const response = (await createClinicMutation.mutateAsync({
        site,
        data,
      })) as {
        message?: string;
        data?: { id?: number };
      };

      toast.success('更新しました。');

      const clinicId = response?.data?.id;

      // After creating clinic, redirect to edit screen
      if (clinicId) {
        navigate(`/${site}/admin/clinics/${clinicId}/edit`, { replace: true });
      } else {
        // Fallback: redirect to clinics list if id is missing
        navigate(`/${site}/admin/clinics`, { replace: true });
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as ErrorWithResponse;
        const data = axiosError.response?.data;
        const clinicNumberError = data?.errors?.clinic_number?.[0];
        if (clinicNumberError) {
          setApiFieldErrors({ clinic_number: clinicNumberError });
          return;
        }
      }
      toast.error('エラーが発生しました。');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppContainer className="space-y-5">
      <AppBreadcrumb items={breadcrumbItems} separator="/" />
      <AppCard title="治療院作成" className="w-full">
        <CreateClinicForm
          onSubmit={handleSubmit}
          isLoading={isLoading}
          siteForSiteTypes={isSuperAdminRoute ? undefined : (site ?? undefined)}
          apiFieldErrors={apiFieldErrors}
          onClearFieldError={field =>
            setApiFieldErrors(prev => {
              if (!prev) return null;
              const next = { ...prev };
              delete next[field];
              return Object.keys(next).length ? next : null;
            })
          }
        />
      </AppCard>
    </AppContainer>
  );
};

export default CreateClinicPage;
